import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ApiFunction from '../../utils/api/apiFuntions';
import { useSelector } from 'react-redux';
import { decryptData } from '../../utils/api/encrypted';
import { Spinner } from 'react-bootstrap';
import { Avatar } from '../icons/icons';

// Add this utility function at the top of your file or import from utils
const formatWhatsAppTime = (timestamp) => {
    if (!timestamp) return '';

    const messageDate = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());

    // Check if it's today
    if (messageDateOnly.getTime() === today.getTime()) {
        return messageDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    }

    // Check if it's yesterday
    if (messageDateOnly.getTime() === yesterday.getTime()) {
        return 'Yesterday';
    }

    // Check if it's within the last 7 days
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    if (messageDateOnly > weekAgo) {
        return messageDate.toLocaleDateString('en-US', { weekday: 'long' });
    }

    // For older messages, show date
    return messageDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit'
    });
};

// Badge color function based on status (aligned with comments; tokens from color.css)
const getBadgeColor = (statusOnline, statusCheck) => {
    // (1) checkout and online -> green
    if (statusCheck === 'checkout' && statusOnline === 'online') {
        return 'var(--status-green)';
    }
    // (2) checkin and online -> primary blue
    if (statusCheck === 'checkin' && statusOnline === 'online') {
        return 'var(--primary)';
    }
    // (3) checkin and offline -> grey
    if (statusCheck === 'checkin' && statusOnline === 'offline') {
        return 'var(--status-grey)';
    }
    // (4) checkout and offline -> black
    if (statusCheck === 'checkout' && statusOnline === 'offline') {
        return 'var(--status-offline)';
    }
    // Default fallback
    return 'var(--status-grey)';
};

const PersonalChatList = () => {
    const { post } = ApiFunction();
    const encryptedToken = useSelector(state => state.appData?.userToken);
    const userToken = decryptData(encryptedToken);
    const [chatUsers, setChatUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const getChatUsers = async (showLoader = false) => {
            const data = {
                type: "get_user_conversations",
                user_id: userToken
            }
            
            // Only show loader if explicitly requested
            if (showLoader) {
                setLoading(true);
            }
            
            await post('', data)
                .then(res => {
                    if (res?.result && Array.isArray(res?.conversations)) {
                        setChatUsers(res?.conversations)
                    } else {
                        setChatUsers([])
                    }
                })
                .catch(err => {
                    console.log("there is an error fetching chat users =>", err);
                    setChatUsers([]);
                })
                .finally(() => {
                    if (showLoader) {
                        setLoading(false);
                    }
                    setIsInitialLoad(false);
                })
        }
        
        // Initial load with loader
        getChatUsers(isInitialLoad);

        // Set up an interval to call the API every 10 seconds without loader
        const interval = setInterval(() => {
            getChatUsers(false);
        }, 10000);

        // Clean up the interval when the component unmounts
        return () => {
            clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userToken])

    return (
        <>
            <div className='private_message_group my-4'>
                {loading ? <>
                    <div className='h-100 w-full flex items-center justify-center'>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </> : <>
                    {(!chatUsers || chatUsers.filter(v => v?.type === 'personal').length === 0) && (
                        <div className='flex flex-col items-center justify-center py-10 px-4 text-center'>
                            <p className='britti_medium fs_16 mb-1'>No conversations yet</p>
                            <p className='txt_grey fs_14 mb-0'>Check in nearby to meet people and start chatting.</p>
                        </div>
                    )}
                    {chatUsers?.map((value, index) => {
                        // Return empty if type is not 'personal'
                        if (value?.type !== 'personal') {
                            return null;
                        }

                        // Get dynamic badge color
                        const badgeColor = getBadgeColor(
                            value?.other_user?.status_online,
                            value?.other_user?.status_check
                        );

                        return (
                            <button onClick={() => navigate(`/private/${value?.id}`, { state: { otherUser: value?.other_user } })} className='' key={index}>
                                <div className="ChatList" >
                                    <div className='relative'>
                                        <img src={value?.other_user?.image ? value?.other_user?.image : Avatar} alt="" />
                                        <div
                                            className='max-h-[18px] max-w-[18px] min-h-[18px] min-w-[18px] border-[4px] border-[#fff] position-absolute right-[-4px] bottom-1'
                                            style={{
                                                borderRadius: '50%',
                                                backgroundColor: badgeColor
                                            }}
                                        ></div>
                                    </div>
                                    <div className='w-full'>
                                        <div className='flex items-center justify-between'>
                                            <h1 className='britti_medium fs_16 mb-[13px]'>{value?.other_user?.name ?? "Traveler"}</h1>
                                            <span className='fs_14 txt_grey'>
                                                {formatWhatsAppTime(value?.last_message_time)}
                                            </span>
                                        </div>
                                        <p className='mb-0 txt_grey text-clamp-1 text-start max-w-[90%]'>{value?.last_message ? value?.last_message : "You haven't messaged each other yet"}</p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </>}
            </div>
        </>
    )
}

export default PersonalChatList